import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import FunProgress from '../ui/FunProgress';
import HistorySection from '../history/HistorySection';
import {
  FileVideo, Upload, AlertCircle, Trash2, Plus,
  Download, Languages, RefreshCw, CheckCircle, FileText, Clock,
} from 'lucide-react';

interface Segment {
  id: number;
  start: number;
  end: number;
  text: string;
}

interface SubStyle {
  position: string;
  fontSize: number;
  fontColor: string;
  bgOpacity: number;
}

// ASS numpad alignment grid (row-major, top to bottom)
const POS_GRID = [
  [{ v: '7', label: 'TL' }, { v: '8', label: 'TC' }, { v: '9', label: 'TR' }],
  [{ v: '4', label: 'ML' }, { v: '5', label: 'MC' }, { v: '6', label: 'MR' }],
  [{ v: '1', label: 'BL' }, { v: '2', label: 'BC' }, { v: '3', label: 'BR' }],
];

const COLORS = [
  { hex: '#ffffff', label: 'White' },
  { hex: '#ffff00', label: 'Yellow' },
  { hex: '#00ffff', label: 'Cyan' },
  { hex: '#00ff00', label: 'Lime' },
  { hex: '#ff8800', label: 'Orange' },
];

const TRANSCRIBE_LANGS = [
  { value: 'auto', label: 'Auto-detect' },
  // ── Mixed / Code-switched (recommended for Indian content) ──
  { value: 'kn_mixed', label: '🔀 Kanglish — Kannada + English mixed (recommended)' },
  { value: 'hi_mixed', label: '🔀 Hinglish — Hindi + English mixed' },
  { value: 'ta_mixed', label: '🔀 Tanglish — Tamil + English mixed' },
  { value: 'te_mixed', label: '🔀 Tenglish — Telugu + English mixed' },
  { value: 'ml_mixed', label: '🔀 Manglish — Malayalam + English mixed' },
  // ── Pure languages ──
  { value: 'kn', label: 'Kannada (pure)' },
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi (pure)' },
  { value: 'ta', label: 'Tamil (pure)' },
  { value: 'te', label: 'Telugu (pure)' },
  { value: 'ml', label: 'Malayalam (pure)' },
  { value: 'mr', label: 'Marathi' },
  { value: 'bn', label: 'Bengali' },
  { value: 'gu', label: 'Gujarati' },
  { value: 'pa', label: 'Punjabi' },
  { value: 'ar', label: 'Arabic' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
];

const TRANSLATE_LANGS = [
  { value: 'english', label: 'English' },
  { value: 'kannada', label: 'Kannada' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'tamil', label: 'Tamil' },
  { value: 'telugu', label: 'Telugu' },
  { value: 'malayalam', label: 'Malayalam' },
  { value: 'marathi', label: 'Marathi' },
  { value: 'bengali', label: 'Bengali' },
  { value: 'gujarati', label: 'Gujarati' },
  { value: 'punjabi', label: 'Punjabi' },
  { value: 'arabic', label: 'Arabic' },
  { value: 'french', label: 'French' },
  { value: 'spanish', label: 'Spanish' },
  { value: 'german', label: 'German' },
  { value: 'japanese', label: 'Japanese' },
  { value: 'korean', label: 'Korean' },
  { value: 'chinese', label: 'Chinese (Simplified)' },
  { value: 'portuguese', label: 'Portuguese' },
  { value: 'russian', label: 'Russian' },
  { value: 'kanglish', label: 'Kanglish (Kannada + English)' },
  { value: 'hinglish', label: 'Hinglish (Hindi + English)' },
  { value: 'tanglish', label: 'Tanglish (Tamil + English)' },
  { value: 'manglish', label: 'Manglish (Malayalam + English)' },
  { value: 'tenglish', label: 'Tenglish (Telugu + English)' },
  { value: 'benglish', label: 'Benglish (Bengali + English)' },
];

const DEFAULT_STYLE: SubStyle = { position: '2', fontSize: 48, fontColor: '#ffffff', bgOpacity: 0.5 };

// ── Subtitle export helpers ───────────────────────────────────────────────────

function fmtSRT(t: number): string {
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = Math.floor(t % 60);
  const ms = Math.round((t - Math.floor(t)) * 1000);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')},${String(ms).padStart(3,'0')}`;
}

function fmtVTT(t: number): string {
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = (t % 60).toFixed(3);
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${s.padStart(6,'0')}`;
}

function toSRT(segs: Segment[]): string {
  return segs.map((seg, i) =>
    `${i + 1}\n${fmtSRT(seg.start)} --> ${fmtSRT(seg.end)}\n${seg.text.trim()}`
  ).join('\n\n') + '\n';
}

function toVTT(segs: Segment[]): string {
  const body = segs.map(seg =>
    `${fmtVTT(seg.start)} --> ${fmtVTT(seg.end)}\n${seg.text.trim()}`
  ).join('\n\n');
  return `WEBVTT\n\n${body}\n`;
}

function hexToASS(hex: string, alpha = '00'): string {
  const h = hex.replace('#', '').padEnd(6, '0');
  return `&H${alpha}${h.substring(4,6)}${h.substring(2,4)}${h.substring(0,2)}&`;
}

function toASS(segs: Segment[], style: SubStyle): string {
  const alignment = parseInt(style.position || '2', 10);
  const primaryColor = hexToASS(style.fontColor || '#ffffff');
  const backAlpha = Math.round((1 - (style.bgOpacity ?? 0.5)) * 255).toString(16).toUpperCase().padStart(2,'0');
  const backColor = `&H${backAlpha}000000&`;
  const borderStyle = (style.bgOpacity ?? 0.5) > 0 ? 3 : 1;
  const outline = (style.bgOpacity ?? 0.5) > 0 ? 0 : 2;

  const fmt = (t: number) => {
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const s = Math.floor(t % 60);
    const cs = Math.round((t - Math.floor(t)) * 100);
    return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}.${String(cs).padStart(2,'0')}`;
  };

  const dialogues = segs.map(seg =>
    `Dialogue: 0,${fmt(seg.start)},${fmt(seg.end)},Default,,0,0,0,,${seg.text.trim().replace(/\n/g, '\\N')}`
  ).join('\n');

  return `[Script Info]
ScriptType: v4.00+
PlayResX: 1280
PlayResY: 720
WrapStyle: 0
ScaledBorderAndShadow: yes

[V4+ Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding
Style: Default,Arial,${style.fontSize},${primaryColor},&H00FFFFFF&,&H00000000&,${backColor},0,0,0,0,100,100,0,0,${borderStyle},${outline},0,${alignment},10,10,20,1

[Events]
Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
${dialogues}`;
}

function downloadBlob(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// CSS position classes for the preview overlay
function overlayClasses(pos: string): string {
  const vert = ['7', '8', '9'].includes(pos) ? 'top-3' : ['4', '5', '6'].includes(pos) ? 'top-1/2 -translate-y-1/2' : 'bottom-3';
  const horiz = ['7', '4', '1'].includes(pos) ? 'left-3 text-left' : ['9', '6', '3'].includes(pos) ? 'right-3 text-right' : 'left-1/2 -translate-x-1/2 text-center';
  return `absolute pointer-events-none ${vert} ${horiz}`;
}

const VideoSubtitles = () => {
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [transcribeLang, setTranscribeLang] = useState('kn_mixed');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translateLang, setTranslateLang] = useState('english');
  const [burning, setBurning] = useState(false);
  const [burnProgress, setBurnProgress] = useState(0);
  const [burnStatus, setBurnStatus] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [style, setStyle] = useState<SubStyle>(DEFAULT_STYLE);
  const [isDragging, setIsDragging] = useState(false);
  // Restored history info — shown in a banner when state is loaded from history
  const [restoredFrom, setRestoredFrom] = useState<{ videoName: string; language: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  // ── Shared restore logic ─────────────────────────────────────────────────────
  const applySnapshot = useCallback((snapshotStr: string) => {
    try {
      const snap = JSON.parse(snapshotStr);
      if (!Array.isArray(snap.segments) || snap.segments.length === 0) return;
      setSegments(snap.segments);
      if (snap.language) setTranscribeLang(snap.language);
      if (snap.style) setStyle(snap.style);
      setVideoFile(null);
      setVideoUrl(null);
      setDownloadUrl(null);
      setError(null);
      setRestoredFrom({
        videoName: snap.videoName || 'previous video',
        language: snap.language || 'auto',
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      // Ignore parse errors
    }
  }, []);

  // ── Restore from history on mount (navigated from another page via HistoryPanel) ──
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('pendingHistoryRestore');
      if (!raw) return;
      sessionStorage.removeItem('pendingHistoryRestore');
      const { toolId, snapshot } = JSON.parse(raw);
      if (toolId === 'video-subtitles' && snapshot) applySnapshot(snapshot);
    } catch {
      // Ignore
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Restore from history via window event (already on this page when clicked) ──
  useEffect(() => {
    const handler = (e: Event) => {
      const { toolId, snapshot } = (e as CustomEvent).detail || {};
      if (toolId === 'video-subtitles' && snapshot) applySnapshot(snapshot);
    };
    window.addEventListener('aadinHistoryRestore', handler);
    return () => window.removeEventListener('aadinHistoryRestore', handler);
  }, [applySnapshot]);

  const handleFile = useCallback((file: File) => {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoFile(file);
    setVideoUrl(URL.createObjectURL(file));
    setSegments([]);
    setError(null);
    setDownloadUrl(null);
    setCurrentTime(0);
  }, [videoUrl]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('video/')) handleFile(file);
    else setError('Please drop a video file (MP4, MOV, AVI, MKV, WebM)');
  };

  const handleTranscribe = async () => {
    if (!videoFile) return;
    setTranscribing(true);
    setError(null);
    setSegments([]);
    setDownloadUrl(null);
    try {
      const form = new FormData();
      form.append('file', videoFile);
      form.append('language', transcribeLang);
      const resp = await fetch('/api/subtitles/transcribe', { method: 'POST', body: form });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Transcription failed');
      setSegments(data.segments || []);
    } catch (err: any) {
      setError(err.message || 'Transcription failed');
    } finally {
      setTranscribing(false);
    }
  };

  const handleTranslateAll = async () => {
    if (!segments.length) return;
    setTranslating(true);
    setError(null);
    try {
      const resp = await fetch('/api/subtitles/translate-segments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ segments, targetLanguage: translateLang }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Translation failed');
      setSegments(data.segments);
    } catch (err: any) {
      setError(err.message || 'Translation failed');
    } finally {
      setTranslating(false);
    }
  };

  const handleBurn = async () => {
    if (!videoFile || !segments.length) return;
    setBurning(true);
    setBurnProgress(0);
    setBurnStatus('');
    setError(null);
    setDownloadUrl(null);
    try {
      const form = new FormData();
      form.append('video', videoFile);
      form.append('segments', JSON.stringify(segments));
      form.append('style', JSON.stringify(style));

      // Upload with XHR to track upload progress
      setBurnProgress(5); setBurnStatus('Uploading video...');
      const blob = await new Promise<Blob>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', '/api/subtitles/burn');
        xhr.responseType = 'blob';
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            const pct = Math.round((e.loaded / e.total) * 30);
            setBurnProgress(5 + pct);
            setBurnStatus(`Uploading... ${(e.loaded / 1024 / 1024).toFixed(1)} MB / ${(e.total / 1024 / 1024).toFixed(1)} MB`);
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 400) { reject(new Error(`HTTP ${xhr.status}`)); return; }
          resolve(xhr.response);
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
        xhr.send(form);
      });

      // Simulate burn progress (ffmpeg runs server-side, no progress stream)
      setBurnProgress(40); setBurnStatus('Burning subtitles with ffmpeg...');
      const interval = setInterval(() => {
        setBurnProgress(p => {
          if (p >= 90) { clearInterval(interval); return 90; }
          return p + 1;
        });
        setBurnStatus('Re-encoding video with subtitles...');
      }, 2000);

      clearInterval(interval);
      setBurnProgress(100); setBurnStatus('Done!');
      setDownloadUrl(URL.createObjectURL(blob));
    } catch (err: any) {
      setError(err.message || 'Subtitle burning failed');
    } finally {
      setBurning(false);
    }
  };

  const updateSeg = (id: number, field: 'text' | 'start' | 'end', value: string) => {
    setSegments(prev => prev.map(s =>
      s.id === id ? { ...s, [field]: field === 'text' ? value : parseFloat(value) || s[field as 'start' | 'end'] } : s
    ));
  };

  // Current visible segment during playback
  const activeSegment = segments.find(s => s.start <= currentTime && s.end >= currentTime);

  return (
    <div className="space-y-6">
      <div className="tool-hero">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#216974] to-[#216974] flex items-center justify-center shadow-lg shadow-[#54c4b6]/30">
            <FileVideo size={16} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2.5 py-1 rounded-full">AI Subtitles</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Video Subtitles</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload video → AI transcribes with timestamps → edit each line → translate to any language (Kanglish, Hinglish…) → burn real subtitles permanently into video.
        </p>
      </div>

      {/* Restored-from-history banner */}
      {restoredFrom && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl"
          style={{
            background: 'linear-gradient(135deg, rgba(84,196,182,0.12) 0%, rgba(33,105,116,0.10) 100%)',
            border: '1px solid rgba(84,196,182,0.25)',
          }}
        >
          <div className="flex items-center gap-2.5 min-w-0">
            <Clock size={15} className="text-[#54c4b6] shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#216974] truncate">
                Restored from history
              </p>
              <p className="text-xs text-gray-500 truncate">
                {restoredFrom.videoName}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setRestoredFrom(null);
              setSegments([]);
              setVideoFile(null);
              setVideoUrl(null);
              setDownloadUrl(null);
              setError(null);
            }}
            className="shrink-0 text-xs text-gray-400 hover:text-gray-700 px-3 py-1.5 rounded-xl transition-colors"
            style={{ background: 'rgba(255,255,255,0.7)' }}
          >
            Start Fresh
          </button>
        </motion.div>
      )}

      {/* Upload zone */}
      {!videoFile && (
        <Card>
          <div
            onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-16 text-center cursor-pointer transition-all ${
              isDragging ? 'border-[#54c4b6] bg-[#216974]' : 'border-gray-200 hover:border-[#54c4b6] hover:bg-[#216974]/30'
            }`}
          >
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
            <Upload size={44} className={`mx-auto mb-4 transition-colors ${isDragging ? 'text-[#216974]' : 'text-gray-300'}`} />
            <p className="font-semibold text-gray-700 text-lg">Drop your video here or click to browse</p>
            <p className="text-sm text-gray-400 mt-2">MP4, MOV, AVI, MKV, WebM · Up to 500 MB</p>
          </div>
        </Card>
      )}

      {/* Workspace */}
      {videoFile && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">

            {/* Left: Video preview + transcribe controls */}
            <div className="lg:col-span-3 space-y-4">
              <Card>
                {/* Video with real-time subtitle overlay */}
                <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                  <video
                    ref={videoRef}
                    src={videoUrl || ''}
                    controls
                    className="w-full h-full object-contain"
                    onTimeUpdate={e => setCurrentTime(e.currentTarget.currentTime)}
                  />
                  {/* Live subtitle preview */}
                  {activeSegment && (
                    <div className={overlayClasses(style.position)}>
                      <span
                        className="font-semibold rounded px-2 py-0.5 whitespace-pre-wrap"
                        style={{
                          color: style.fontColor,
                          fontSize: `${Math.round(style.fontSize * 0.35)}px`,
                          backgroundColor: style.bgOpacity > 0 ? `rgba(0,0,0,${style.bgOpacity})` : 'transparent',
                          textShadow: style.bgOpacity === 0 ? '0 1px 4px rgba(0,0,0,1)' : 'none',
                          lineHeight: 1.4,
                        }}
                      >
                        {activeSegment.text}
                      </span>
                    </div>
                  )}
                </div>

                {/* File info + reset */}
                <div className="flex items-center justify-between mt-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 truncate max-w-xs">{videoFile.name}</p>
                    <p className="text-xs text-gray-400">{(videoFile.size / 1024 / 1024).toFixed(1)} MB</p>
                  </div>
                  <button
                    onClick={() => { setVideoFile(null); setVideoUrl(null); setSegments([]); setError(null); setDownloadUrl(null); }}
                    className="text-xs text-[#216974] hover:text-[#216974] font-medium flex items-center gap-1 transition-colors"
                  >
                    <RefreshCw size={12} /> Change video
                  </button>
                </div>

                {/* Transcribe controls (shown only before first transcription) */}
                {segments.length === 0 && (
                  <div className="flex gap-3 mt-3">
                    <select value={transcribeLang} onChange={e => setTranscribeLang(e.target.value)}
                      className="flex-1 bg-white border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#54c4b6]/20">
                      {TRANSCRIBE_LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                    <Button onClick={handleTranscribe} isLoading={transcribing} disabled={transcribing}
                      leftIcon={<Languages size={15} />} className="shrink-0">
                      {transcribing ? 'Transcribing…' : 'Transcribe'}
                    </Button>
                  </div>
                )}

                {/* Re-transcribe button after segments exist */}
                {segments.length > 0 && (
                  <button onClick={handleTranscribe} disabled={transcribing}
                    className="mt-3 flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#216974] transition-colors">
                    <RefreshCw size={11} /> Re-transcribe
                  </button>
                )}
              </Card>
            </div>

            {/* Right: Style panel */}
            <div className="lg:col-span-2">
              <Card>
                <div className="space-y-5">
                  <p className="text-sm font-semibold text-gray-800">Subtitle Style</p>

                  {/* Position grid */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Position</p>
                    <div className="inline-grid grid-cols-3 gap-1.5">
                      {POS_GRID.map(row =>
                        row.map(cell => (
                          <button key={cell.v} onClick={() => setStyle(s => ({ ...s, position: cell.v }))}
                            title={cell.label}
                            className={`w-11 h-11 rounded-lg text-[11px] font-bold transition-all ${
                              style.position === cell.v
                                ? 'bg-[#216974] text-white shadow-md shadow-[#54c4b6]/30'
                                : 'bg-gray-100 text-gray-500 hover:bg-[#216974] hover:text-[#216974]'
                            }`}
                          >
                            {cell.label}
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Font size */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Font Size</p>
                      <span className="text-xs font-bold text-[#216974]">{style.fontSize}px</span>
                    </div>
                    <input type="range" min={18} max={96} step={2} value={style.fontSize}
                      onChange={e => setStyle(s => ({ ...s, fontSize: parseInt(e.target.value) }))}
                      className="w-full accent-[#216974]" />
                  </div>

                  {/* Font color */}
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Text Color</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {COLORS.map(c => (
                        <button key={c.hex} onClick={() => setStyle(s => ({ ...s, fontColor: c.hex }))}
                          title={c.label}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            style.fontColor === c.hex ? 'border-[#54c4b6] scale-110 shadow-md' : 'border-gray-200'
                          }`}
                          style={{ backgroundColor: c.hex }}
                        />
                      ))}
                      <input type="color" value={style.fontColor}
                        onChange={e => setStyle(s => ({ ...s, fontColor: e.target.value }))}
                        className="w-8 h-8 rounded-full cursor-pointer border-2 border-gray-200 transition-all"
                        title="Custom color"
                      />
                    </div>
                  </div>

                  {/* Background opacity */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Background</p>
                      <span className="text-xs font-bold text-[#216974]">
                        {style.bgOpacity === 0 ? 'Off (outline only)' : `${Math.round(style.bgOpacity * 100)}%`}
                      </span>
                    </div>
                    <input type="range" min={0} max={1} step={0.1} value={style.bgOpacity}
                      onChange={e => setStyle(s => ({ ...s, bgOpacity: parseFloat(e.target.value) }))}
                      className="w-full accent-[#216974]" />
                    <p className="text-[10px] text-gray-400 mt-1">0% = outline only · 100% = solid black box</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Segment editor */}
          <AnimatePresence>
            {segments.length > 0 && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
                <Card>
                  <div className="space-y-4">
                    {/* Header + translate bar */}
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-gray-800">Subtitle Editor</p>
                          <span className="text-xs font-medium text-[#216974] bg-[#54c4b6]/15 px-2 py-0.5 rounded-full">
                            {segments.length} segments
                          </span>
                          <CheckCircle size={14} className="text-[#54c4b6]" />
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">Edit any text. Timings are in seconds — adjust as needed.</p>
                      </div>
                      {/* Translate all */}
                      <div className="flex items-center gap-2">
                        <select value={translateLang} onChange={e => setTranslateLang(e.target.value)}
                          className="bg-white border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#54c4b6]/20">
                          {TRANSLATE_LANGS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                        </select>
                        <Button onClick={handleTranslateAll} isLoading={translating} disabled={translating}
                          size="sm" leftIcon={<Languages size={13} />}>
                          Translate All
                        </Button>
                      </div>
                    </div>

                    {/* Segments list */}
                    <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
                      {segments.map((seg, idx) => (
                        <motion.div key={seg.id}
                          initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                          className={`flex items-start gap-3 p-3 rounded-xl border transition-colors group ${
                            activeSegment?.id === seg.id
                              ? 'border-[#54c4b6] bg-[#216974]/60'
                              : 'border-gray-100 bg-gray-50/60 hover:border-[#54c4b6]'
                          }`}
                        >
                          {/* Number */}
                          <span className="text-[11px] font-bold text-[#216974] w-5 text-right shrink-0 mt-2">
                            {idx + 1}
                          </span>

                          {/* Timing inputs */}
                          <div className="flex items-center gap-1 shrink-0 mt-1">
                            <input value={seg.start.toFixed(1)}
                              onChange={e => updateSeg(seg.id, 'start', e.target.value)}
                              className="w-14 text-center text-[11px] font-mono bg-white border border-gray-200 rounded-lg px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#54c4b6]"
                            />
                            <span className="text-[10px] text-gray-300">→</span>
                            <input value={seg.end.toFixed(1)}
                              onChange={e => updateSeg(seg.id, 'end', e.target.value)}
                              className="w-14 text-center text-[11px] font-mono bg-white border border-gray-200 rounded-lg px-1 py-1.5 focus:outline-none focus:ring-1 focus:ring-[#54c4b6]"
                            />
                          </div>

                          {/* Text */}
                          <textarea value={seg.text}
                            onChange={e => updateSeg(seg.id, 'text', e.target.value)}
                            rows={1}
                            className="flex-1 text-sm text-gray-700 bg-white border border-gray-200 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#54c4b6]/20 focus:border-[#54c4b6] resize-none overflow-hidden"
                            onInput={e => {
                              const el = e.target as HTMLTextAreaElement;
                              el.style.height = 'auto';
                              el.style.height = el.scrollHeight + 'px';
                            }}
                          />

                          {/* Delete */}
                          <button onClick={() => setSegments(p => p.filter(s => s.id !== seg.id))}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-[#216974] hover:text-[#216974] mt-1.5 shrink-0">
                            <Trash2 size={14} />
                          </button>
                        </motion.div>
                      ))}
                    </div>

                    {/* Add segment */}
                    <button onClick={() => {
                      const last = segments[segments.length - 1];
                      const newStart = last ? last.end + 0.1 : 0;
                      setSegments(p => [...p, { id: Date.now(), start: newStart, end: newStart + 2, text: '' }]);
                    }} className="flex items-center gap-1.5 text-xs text-[#216974] hover:text-[#216974] font-medium transition-colors">
                      <Plus size={13} /> Add subtitle segment
                    </button>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error */}
          <AnimatePresence>
            {error && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="alert-error">
                <AlertCircle size={15} className="shrink-0" />
                <p className="text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Download subtitle files */}
          {segments.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Card>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <FileText size={15} className="text-[#54c4b6]" />
                      Download Subtitle File
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Download the current subtitles as a standalone file — useful for uploading to YouTube, VLC, etc.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadBlob(toSRT(segments), 'subtitles.srt', 'text/plain')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#216974]/10 text-[#216974] text-sm font-semibold hover:bg-[#216974]/20 transition-colors border border-[#216974]/20"
                    >
                      <Download size={13} /> SRT
                    </button>
                    <button
                      onClick={() => downloadBlob(toVTT(segments), 'subtitles.vtt', 'text/vtt')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#216974]/10 text-[#216974] text-sm font-semibold hover:bg-[#216974]/20 transition-colors border border-[#216974]/20"
                    >
                      <Download size={13} /> VTT
                    </button>
                    <button
                      onClick={() => downloadBlob(toASS(segments, style), 'subtitles.ass', 'text/plain')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#216974]/10 text-[#216974] text-sm font-semibold hover:bg-[#216974]/20 transition-colors border border-[#216974]/20"
                    >
                      <Download size={13} /> ASS
                    </button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Action bar */}
          {segments.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col sm:flex-row gap-3">
              <Button onClick={handleBurn} size="lg" className="flex-1"
                isLoading={burning} disabled={burning || !segments.length}
                leftIcon={<FileVideo size={18} />}>
                {burning ? 'Burning subtitles into video…' : 'Burn Subtitles into Video'}
              </Button>

              <AnimatePresence>
                {downloadUrl && (
                  <motion.a href={downloadUrl} download="subtitled_video.mp4" className="flex-1"
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
                    <Button size="lg" variant="success" className="w-full" leftIcon={<Download size={18} />}>
                      Download Subtitled Video
                    </Button>
                  </motion.a>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Burn progress */}
          <AnimatePresence>
            {burning && burnProgress > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="glass-card rounded-2xl p-4">
                <FunProgress percent={burnProgress} tool="video" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* History — past transcription sessions */}
      <HistorySection
        toolId="video-subtitles"
        onUseHistory={(entry) => {
          try {
            if (!entry.snapshot) return;
            const snap = JSON.parse(entry.snapshot);
            if (Array.isArray(snap.segments) && snap.segments.length > 0) {
              setSegments(snap.segments);
              if (snap.language) setTranscribeLang(snap.language);
              if (snap.style) setStyle(snap.style);
              setVideoFile(null);
              setVideoUrl(null);
              setDownloadUrl(null);
              setError(null);
              setRestoredFrom({
                videoName: snap.videoName || 'previous video',
                language: snap.language || 'auto',
              });
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          } catch {
            // Ignore
          }
        }}
      />
    </div>
  );
};

export default VideoSubtitles;
